import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverPortal,
} from "@radix-ui/react-popover";
import { Circle, CircleHelp, Info } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { twMerge } from "tailwind-merge";

type PopoverContentProps = React.ComponentProps<typeof PopoverContent>;

export function HelpButton({
  title,
  trigger,
  infoIcon,
  children,
  triggerClassName,
  size = 18,
  large,
  ping,
  side,
  sideOffset,
}: {
  trigger?: (size: number, className?: string) => React.ReactNode;
  infoIcon?: (size: number, className?: string) => React.ReactNode;
  title: string | React.ReactNode;
  children: React.ReactNode;
  triggerClassName?: string;
  size?: number;
  large?: true;
  ping?: true;
  side?: PopoverContentProps["side"];
  sideOffset?: PopoverContentProps["sideOffset"];
}) {
  const triggerClass = twMerge(
    "ml-2 relative inline-flex opacity-60 group-hover:opacity-100",
    triggerClassName
  );
  const maxW = large
    ? "max-w-[calc(min(90vw,600px))]"
    : "max-w-[calc(min(90vw,500px))]";
  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <div className="hover:cursor-pointer inline-flex group">
            {ping ? (
              <Circle
                size={size}
                className={twMerge(
                  "ml-2 absolute inline-flex animate-ping opacity-50 group-hover:opacity-100",
                  triggerClassName
                )}
              />
            ) : null}
            {trigger ? (
              trigger(size, triggerClass)
            ) : (
              <CircleHelp size={size} className={triggerClass} />
            )}
          </div>
        </PopoverTrigger>
        <PopoverPortal>
          <PopoverContent
            sideOffset={sideOffset}
            side={side}
            asChild
            className={`z-[9999] ${maxW} rounded-xl text-base border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95`}
          >
            <Card className="shadow-md">
              <CardHeader>
                <CardTitle className="flex gap-1 leading-2 items-center">
                  {infoIcon ? (
                    infoIcon(36, "mr-2")
                  ) : (
                    <Info size={36} className="mr-2" />
                  )}{" "}
                  {title}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                {children}
              </CardContent>
            </Card>
          </PopoverContent>
        </PopoverPortal>
      </Popover>
    </>
  );
}
